/**
 * useTenantSessionGuard Hook
 *
 * Web tenancy is a query param (`tenant-handle`), so a single origin serves
 * every tenant and the stored session is shared across them. That means an
 * authenticated member of one tenant can navigate to a *different* tenant's
 * portal (e.g. a link/ad) while still carrying their session.
 *
 * Auth is tenant-scoped — a session for tenant A is meaningless at tenant B —
 * so the right move is a clean break: log out and let them browse the new
 * tenant as a guest, with a brief "Viewing a new club" toast. This is distinct
 * from the same-tenant wrong-club case (handled by `useClubAccess`, which gates
 * CTAs rather than logging out).
 *
 * Signal: the session's `user.tenantId` vs. the currently-viewed club's
 * `tenantId` (both numeric ids — no handle needed). We only act once the viewed
 * club has loaded, so we never log out on a transient empty state.
 *
 * @module club/hooks
 */

import { useEffect, useRef } from 'react';
import { butterToast } from '@butter/ui/patterns';
import { useAuth } from '~/services/auth';
import { captureException } from '@butter/monitoring/web';
import { buildClubEntryUrl } from '~/lib/queryParams';
import { useClubQuery } from './useClubQuery';

export function useTenantSessionGuard(): void {
  const { isAuthenticated, user, logout } = useAuth();
  const { data: club } = useClubQuery();

  // Guard against re-running the logout while auth state tears down.
  const loggedOut = useRef(false);

  useEffect(() => {
    if (!isAuthenticated || !user || !club) return;

    const sessionTenantId = Number(user.tenantId);
    const viewedTenantId = Number(club.tenantId);
    if (!Number.isFinite(sessionTenantId) || !Number.isFinite(viewedTenantId)) return;
    if (sessionTenantId === viewedTenantId) return;

    if (loggedOut.current) return;
    loggedOut.current = true;

    // Capture the home tenant handle + club before logout clears the session,
    // so the toast can route back into their own club's portal (club id is
    // required for the app to resolve a club on landing).
    //
    // `tenantHandle` comes from /me, which AuthProvider re-fetches on every load,
    // so it's present in practice. The null path is the narrow pre-/me edge (a
    // session whose user object came only from the login response); the logout
    // still fires — only the best-effort "Back to your club" link is omitted.
    const homeTenantHandle = typeof user.tenantHandle === 'string' ? user.tenantHandle : null;
    const homeClubId = user.selectedClubId ?? null;

    butterToast.info('Viewing a new club', null, {
      action: homeTenantHandle
        ? {
            label: 'Back to your club',
            // Full navigation so the app re-initializes under the home tenant.
            onClick: () => {
              window.location.href = buildClubEntryUrl({ tenantHandle: homeTenantHandle, clubId: homeClubId });
            },
          }
        : undefined,
    });
    // Fire-and-forget (the toast lives app-level and survives the unmount), but
    // surface a failed session-revoke to monitoring rather than swallowing it.
    logout().catch((err) =>
      captureException(err, { extra: { context: 'tenant-session-guard-logout' } }),
    );
  }, [isAuthenticated, user, club, logout]);
}
